import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { nanoid } from "nanoid";
import {
  getActivePlan,
  getBriefsStatistics,
  getInvoices,
  getUser,
  getUserBriefs,
  getUsers
} from "../auth/thunks";
import { briefLoaded } from "../brief";
import { createBrief, getAllBriefData } from "../brief/thunks";
import {
  cancelPlan, changeCurrentPlan,
  createContentGapAnalysis,
  createDoc,
  createPlan,
  disableSearchConsole,
  getAllPaddleUsers,
  getCheckoutLink,
  getCurrentUser,
  getPlans,
  getSearchConsoleData,
  getSites,
  getTopBar,
  saveTopBar,
  updateExportInHtml,
  updatePlan,
  updateUserPlan
} from "./thunks";
import { Snack, SystemState, TabType } from "./types";

const plans = [];
const initialState: SystemState = {
  snacks: [],
  currentTab: "brief",
  lastSaved: null,
  user: {
    isLoading: false,
    userCreateDate: "",
    email: "",
    activePlan: {

      numberOfArticlesInPlan: "",
      numberOfBriefs: 0,
      numberOfWordsInPlan: 0,
    },
    isTrialUsed: false,
    isOnboardingShown: false,
    exportInHtml: false,
    stripePlan: undefined,
    paddlePlan: undefined,
    plan: undefined,
    checkoutLink: "",
    planDetails: {
      planName: "",
      planId: "",
      numberOfBriefAtCurrentPlan: 0,
      totalArticlesWritten: 0,
      totalWordsWritten: 0,
    },

    briefs: {
      entities: [],
      loading: {
        ["briefs/requested"]: "idle",
      },
      total: 0,
      limit: 25,
      page: 0,
      paginationTokens: {
        0: { id: "", createdAt: "", cognitoUserName: "" },
      },
    },
    role: "",
  },
  topBar: {
    message: "",
    CTA: "",
    link: "",
    isActive: false,
  },
  topBarSeen: {
    isTopBarSeen: false,
    topBarId: "",
    topBarSeenAt: "",
  },
  invoices: [],
  isUpgradeModalOpen: false,
  isAIWordsUpgradeModalOpen: false,
  aiUpgradeModalText: "",
  plans: [],
  isContentGapReady: false,
  isBriefLimitReached: false,


  users: {
    isLoading: false,
    list: [],
    limit: 25,
    total: 0,
    page: 0,
    paginationTokens: {
      0: "",
    },
  },
  paddleUsers: {
    list: [],
    limit: 50,
    total: 0,
    page: 0,
    paginationTokens: {
      0: "",
    },
    isLoading: false,
  },
  statistics: {
    briefs: {
      isLoading: false,
      list: [],
    },
  },
  briefs: {
    page: 0,
  },
};

const systemSlice = createSlice({
  name: "system",
  initialState,

  reducers: {

    setUserRole: (state, action: PayloadAction<string>) => {
      state.user.role = action.payload;
    },

    resetContentGapReady: (state) => {
      state.isContentGapReady = false;
    }
    ,

    snackAdded: (state, action: PayloadAction<Omit<Snack, "id">>) => {
      state.snacks.push({ ...action.payload, id: nanoid() });
    },
    snackRemoved: (state, action: PayloadAction<string>) => {
      state.snacks = state.snacks.filter(
        (snack) => snack.id !== action.payload
      );
    },
    tabSelected: (state, action: PayloadAction<TabType>) => {
      state.currentTab = action.payload;
    },
    briefSaved: (state, action: PayloadAction<number>) => {
      state.lastSaved = action.payload;
    },
    loginNewPassword: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
    },
    upgradeModalShowed: (state, action: PayloadAction<boolean>) => {
      state.isUpgradeModalOpen = true;
      state.isBriefLimitReached = action.payload;
    },
    upgradeModalHided: (state) => {
      state.isUpgradeModalOpen = false;
    },
    aiWordsUpgradeModalShowed: (state, action: PayloadAction<string>) => {
      state.aiUpgradeModalText = action.payload;
      state.isAIWordsUpgradeModalOpen = true;
    },
    aiWordsUpgradeModalHided: (state) => {
      state.isAIWordsUpgradeModalOpen = false;
    },
    pageChanged: (state, action: PayloadAction<number>) => {
      state.briefs.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(briefLoaded, (state) => {
        state.lastSaved = null;
      })
      .addCase(getAllBriefData.rejected, (state, action) => {
        state.snacks.push({
          duration: 15000,
          message: "Internal error, please refresh page and try again",
          type: "error",
          id: nanoid(),
        });
      })
      .addCase(getBriefsStatistics.fulfilled, (state, action) => {
        state.statistics.briefs.list = action.payload.data;
      })
      .addCase(createBrief.fulfilled, (state, action: PayloadAction<any>) => {
        const currentPlan = state.user.paddlePlan ?? state.user.stripePlan;
        if (currentPlan.numberOfBriefAtCurrentPlan) {
          currentPlan.numberOfBriefAtCurrentPlan += 1;
        }
        if (action?.payload?.info === "limit reached") {
          state.snacks.push({
            duration: 25000,
            message: "Monthly limit reached, please contact support",
            type: "error",
            id: nanoid(),
          });
        }
      })
      .addCase(createDoc.fulfilled, (state, action: PayloadAction<any>) => {
        if (!action.payload.error) {
          state.snacks.push({
            duration: 2000,
            message: "Google Document created",
            type: "success",
            id: nanoid(),
          });
        }
      })
      .addCase(getSites.fulfilled, (state, action: PayloadAction<any>) => {
        if (!action.payload.error) {
          state.snacks.push({
            duration: 5000,
            message: action.payload.message,
            type: action.payload.type,
            id: nanoid(),
          });
        }
      })
      .addCase(getTopBar.fulfilled, (state, action: PayloadAction<any>) => {
        if (!action.payload.error) {
          state.topBar = action.payload;

        }
      })
      .addCase(saveTopBar.fulfilled, (state, action: PayloadAction<any>) => {
        if (!action.payload.error) {
          state.snacks.push({
            duration: 5000,
            message: action.payload.message,
            type: action.payload.type,
            id: nanoid(),
          });
        }
      })
      .addCase(createContentGapAnalysis.pending, (state) => {
        state.snacks.push({
          duration: 5000,
          message: "Creating content gap analysis...",
          type: "info",
          id: nanoid(),
        });
      }
      )
      .addCase(createContentGapAnalysis.fulfilled, (state, action: PayloadAction<any>) => {

        if (!action.payload.error) {
          state.isContentGapReady = true;
          state.snacks.push({
            duration: 5000,
            message: action.payload.message,
            type: action.payload.type,
            id: nanoid(),
          });



        }


      }
      )

      .addCase(getSearchConsoleData.fulfilled, (state, action: PayloadAction<any>) => {
        if (!action.payload.error) {
          state.snacks.push({
            duration: 5000,
            message: action.payload.message,
            type: action.payload.type,
            id: nanoid(),
          });
        }
      }
      )
      .addCase(disableSearchConsole.fulfilled, (state, action: PayloadAction<any>) => {
        if (!action.payload.error) {
          state.snacks.push({
            duration: 5000,
            message: action.payload.message,
            type: "success",
            id: nanoid(),
          });
        }
      }
      )
      .addCase(getActivePlan.fulfilled, (state, action: PayloadAction<any>) => {
        const currentPlan = state.plans.find(
          (plan) => plan.productId === action.payload.plan?.id
        );
        if (currentPlan) {
          currentPlan.isActive = true;
        }



        if (action.payload?.plan?.type === "paddle") {
          state.user.paddlePlan = {
            ...state.user.paddlePlan,
            ...action.payload.plan,
          };
        } else {
          state.user.stripePlan = {
            ...state.user.stripePlan,
            ...action.payload.plan,
          };
        }
      })
      .addCase(
        changeCurrentPlan.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.user.paddlePlan.subscription_plan_id = String(
            action.payload.plan_id
          );
        }
      )
      .addCase(getCheckoutLink.fulfilled, (state, action: PayloadAction<any>) => {
        state.user.checkoutLink = action.payload.checkoutLink;
      }
      )
      .addCase(getInvoices.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload?.invoices?.data) {
          state.invoices = action.payload.invoices.data;
        }
      })
      .addCase(
        getCurrentUser.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.user.paddlePlan = {
            ...action.payload?.plan,
            ...state.user.paddlePlan,
          };
          state.user.exportInHtml =
            typeof action.payload?.exportInHtml === "boolean"
              ? action.payload.exportInHtml
              : state.user.exportInHtml;
          state.invoices = action.payload?.payments?.map((payment) => ({
            ...payment,
            id: payment.subscription_payment_id,
            created: new Date(payment.event_time),
            amount_paid: payment.sale_gross * 100,
            invoice_pdf: payment.receipt_url,
          }));
        }
      )
      .addCase(updateExportInHtml.fulfilled, (state, action: any) => {
        if (typeof action.payload?.exportInHtml === "boolean") {
          state.user.exportInHtml = action.payload.exportInHtml;
        } else if (typeof action.meta?.arg === "boolean") {
          // Fallback to requested value if backend response omits the flag
          state.user.exportInHtml = action.meta.arg;
        }
      })
      .addCase(getUsers.pending, (state, action: PayloadAction<any>) => {
        state.users.isLoading = true;
      })
      .addCase(getAllPaddleUsers.pending, (state) => {
        state.paddleUsers.isLoading = true;
      }
      )
      .addCase(
        getAllPaddleUsers.fulfilled,
        (state, action: PayloadAction<any, any, any>) => {
          if (action?.payload) {
            state.paddleUsers.list = action.payload?.users;
            state.paddleUsers.paginationTokens[action.meta.arg.page + 1] =
              action.payload.paginationToken;
            state.paddleUsers.limit = action.payload.limit ?? 50;
            state.paddleUsers.total = action.payload.total;
            state.paddleUsers.page = action.meta.arg.page;
            state.paddleUsers.isLoading = false;
          }
        }
      )
      .addCase(getUsers.fulfilled, (state, action: PayloadAction<any>) => {
        if (action?.payload) {
          state.users.list = action.payload.users;
          state.users.paginationTokens[state.users.page + 1] =
            action.payload.paginationToken;
          state.users.limit = action.payload.limit ?? 50;
          state.users.total = action.payload.total;
          state.users.isLoading = false;
          state.users.page++;
        }
      })
      .addCase(getUsers.rejected, (state, action: PayloadAction<any>) => {
        state.users.isLoading = false;
      })
      .addCase(createPlan.fulfilled, (state, action: PayloadAction<any>) => {
        state.plans.push(action.payload);
      })
      .addCase(
        cancelPlan.fulfilled,
        (state, action: PayloadAction<any, any, any>) => {
          const currentPlan = state.plans.find(
            (plan) => plan.productId === action.meta.arg.productId
          );

          // if (currentPlan) {
          //   currentPlan.isActive = false;
          // }
        }
      )
      .addCase(getPlans.fulfilled, (state, action: PayloadAction<any>) => {
        if (state.plans.length === 0) {
          const newPlans = action?.payload?.plans?.map((plan) => {
            const isActive = Array.isArray(state.plans)
              ? state.plans.find((item) => item.name === plan.name)?.isActive
              : undefined;
            const publicPlan = Array.isArray(plans)
              ? plans.find((item: any) => item.name === plan.name)
              : undefined;
            if (publicPlan) {
              return {
                ...publicPlan,
                ...plan,
                monthlyPriceId: plan.default_price,
                productId: plan.id,
                active: undefined,
                isActive,
              };
            } else {
              return {
                ...plan,
                active: undefined,
                isActive,
                productId: plan.id,
              };
            }
          });

          state.plans = newPlans.sort(
            (a, b) => Number(a.price) - Number(b.price)
          );
        }
      })
      .addCase(getUser.pending, (state) => {
        state.user.isLoading = true;
      })
      .addCase(getUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.user.userCreateDate = action.payload.UserCreateDate;
        state.user.activePlan = action.payload.activePlan;
        state.user.username = action.payload.Username;
        state.user.planDetails = action.payload.planDetails;
        state.user.email = action.payload.UserAttributes.find(
          (element) => element.Name === "email"
        ).Value;
        state.user.isLoading = false;
      })
      .addCase(
        updateUserPlan.fulfilled,
        (
          state,
          action: PayloadAction<any, string, { arg: { email?: string } }>
        ) => {
          const currentUserIdx = state.paddleUsers.list.findIndex(
            (user) => user.email === decodeURIComponent(action.meta.arg.email)
          );
          state.paddleUsers.list[currentUserIdx].plan = action.payload.plan;
          state.user.activePlan = action.payload.plan;
        }
      )
      .addCase(getUser.rejected, (state) => {
        state.user.isLoading = false;
      })
      .addCase(getUserBriefs.pending, (state) => {
        state.user.briefs.loading["briefs/requested"] = "pending";
      })
      .addCase(getUserBriefs.fulfilled, (state, action: PayloadAction<any>) => {
        state.user.briefs.loading["briefs/requested"] = "finished";
        state.user.briefs.entities = action.payload.briefs?.map((brief) => ({
          ...brief,
          status: "Idle",
        }));
        state.user.briefs.paginationTokens[state.user.briefs.page + 1] =
          action.payload.paginationToken;
        state.user.briefs.limit = action.payload.limit ?? 50;
        state.user.briefs.total = action.payload.totalNumberOfBriefs;
        state.user.briefs.page++;
      })
      .addCase(getUserBriefs.rejected, (state) => {
        state.user.briefs.loading["briefs/requested"] = "finished";
      })
      .addCase(updatePlan.fulfilled, (state, action: PayloadAction<any>) => {
        const currentPlanIdx = state.plans.findIndex(
          (plan) => plan.productId === action.payload.productId
        );
        const newPlan = { ...state.plans[currentPlanIdx], ...action.payload };

        state.plans[currentPlanIdx] = newPlan;
      });
  },

});

export const {
  snackAdded,
  snackRemoved,
  tabSelected,
  briefSaved,
  loginNewPassword,
  upgradeModalShowed,
  upgradeModalHided,
  aiWordsUpgradeModalShowed,
  aiWordsUpgradeModalHided,
  pageChanged,
  resetContentGapReady,
  setUserRole
} = systemSlice.actions;

export default systemSlice.reducer;
